/// A debugging utility, not to be used in type definitions.
// export type _Prettify<T> = { [K in keyof T]: T[K] } & {}

export type EmptyObject = Record<never, never>

export type Override<T, R> = Omit<T, keyof R> & R

// TODO: Use Template Literal Types instead on TS >= 4.1:
// export type ValueOf<T> = `$[T]`
export type ValueOf<T> = T[keyof T]

export type RequiredKeys<T, K extends keyof T> = T & Required<Pick<T, K>>

// NOTE: Use Awaited<T> to get T from MaybePromise<T>
export type MaybePromise<T> = T | Promise<T> | PromiseLike<T>

export type JsonPrimitive = string | number | boolean | null
export type JsonObject = Partial<{ [key: string]: JsonValue }>
export type JsonArray = Array<JsonValue>
export type JsonValue = JsonPrimitive | JsonObject | JsonArray

/**
 * These are the "well-kinded" falsy values, which are commonly ignored in
 * rendering, like in React.
 *
 * It is intentional to not include `0` and `""` in this list, as those values
 * might better be checked more explicitly.
 */
export type ReactFalsy = undefined | null | false

export type AnyFalsy = undefined | null | false | 0 | ""

export type Truthy<T> = Exclude<T, AnyFalsy>

export function filterTruthy<T>(values: T[]): Truthy<T>[] {
  return values.filter((value): value is Truthy<T> => Boolean(value))
}

export function filterNonNullish<T>(values: T[]): NonNullable<T>[] {
  return values.filter((value): value is NonNullable<T> => value != null)
}

export type TruthyObject<T> = Record<string, Truthy<T[keyof T]>>

export function stripUndefined<
  T extends object,
  R = { [key in keyof T]: Exclude<T[keyof T], undefined> },
>(obj: T): R {
  return Object.fromEntries(
    Object.entries(obj).filter((entry) => entry[1] !== undefined),
  ) as R
}

export function isDate(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === "[object Date]"
}

export function isValueOfEnum<V, E extends Record<any, any>>(
  enumType: E,
  value: V | null | undefined,
): value is V {
  if (!value) {
    return false
  }
  return Object.values(enumType).includes(value)
}

export function arraysEqual<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) {
    return false
  }
  return arr1.every((val, index) => val === arr2[index])
}

export function* range(start: number, end: number): Generator<number> {
  if (start >= end) {
    throw new Error(`Invalid range: start (${start}) must be less than end (${end})`)
  }
  for (let i = start; i < end; i++) {
    yield i
  }
}

export function uniqueArray<T>(arr: T[] | null | undefined): T[] {
  return Array.from(new Set(arr))
}

// TODO: Stricter typing to disallow nullish key values.
export function uniqueArrayByKey<T>(arr: T[], key: keyof T): T[] {
  const map = new Map(
    arr.map((item) => {
      const itemKey = item[key]
      if (itemKey == null) {
        throw new Error(
          `Item with key ${String(key)} is null or undefined: ${JSON.stringify(item)}`,
        )
      }
      return [itemKey, item]
    }),
  )
  return Array.from(map.values())
}

export function arrayToObjectByKey<T extends object>(
  arr: T[] | null | undefined,
  key: keyof T,
): Record<string, T> {
  if (!arr) {
    return {}
  }
  return arr.reduce(
    (acc, item) => {
      const itemKey = item[key]
      if (itemKey == null) {
        throw new Error(
          `Item with key ${String(key)} is null or undefined: ${JSON.stringify(item)}`,
        )
      }
      acc[String(itemKey)] = item
      return acc
    },
    {} as Record<string, T>,
  )
}

export function arrayGroupByKey<T extends object>(
  arr: T[] | null | undefined,
  key: keyof T,
): Record<string, T[]> {
  if (!arr) {
    return {}
  }
  return arr.reduce(
    (acc, item) => {
      const itemKey = item[key]
      if (itemKey == null) {
        throw new Error(
          `Item with key ${String(key)} is null or undefined: ${JSON.stringify(item)}`,
        )
      }
      const k = String(itemKey)
      if (!acc[k]) {
        acc[k] = []
      }
      acc[k].push(item)
      return acc
    },
    {} as Record<string, T[]>,
  )
}

export function arrayToMapByKey<T extends object, K extends keyof T>(
  arr: T[] | null | undefined,
  key: K,
): Map<T[K], T> {
  if (!arr) {
    return new Map()
  }
  return arr.reduce((acc, item) => {
    const itemKey = item[key]
    if (itemKey == null) {
      throw new Error(
        `Item with key ${String(key)} is null or undefined: ${JSON.stringify(item)}`,
      )
    }
    acc.set(itemKey, item)
    return acc
  }, new Map<T[K], T>())
}

export function objectPick<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    result[key] = obj[key]
  }
  return result
}
